import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  Package,
  MapPin,
  Calendar,
  CheckCircle2,
  Tags,
  ShoppingCart,
  Lock,
  Award,
  PiggyBank,
  Sparkles,
  Boxes,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Réseau de distribution ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Réseau de distribution
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre réseau à {city.name} et en Île-de-France
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Ne perdez plus vos heures à chercher des contenants de fortune
            ou à charger des liasses poussiéreuses dans un coffre encombré.
            Nous orchestrons le transit complet de vos fournitures
            directement jusqu&apos;à votre adresse vanvéenne, tout en
            étendant notre expertise logistique à l&apos;échelle
            régionale.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Une flotte interne dédiée",
              body: "Nous n'externalisons jamais nos services. Notre propre entreprise prend en charge vos fournitures de déménagement, garantissant un contrôle total de la manipulation et une ponctualité rigoureuse au pied de votre immeuble ou de vos bureaux.",
              Icon: Truck,
            },
            {
              title: "Rayonnement complet en Île-de-France",
              body: `Que votre départ s'effectue depuis ${city.name} ou que vous ayez besoin de points de dépose complémentaires dans la région, notre flotte quadrille quotidiennement l'ensemble des départements de l'Île-de-France pour des livraisons ultra-réactives en circuit court.`,
              Icon: MapPin,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Check-list ultime (4 jalons countdown timeline) ======= */}
    <section className="py-14 md:py-20 bg-white relative overflow-hidden">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
            <Calendar className="h-3.5 w-3.5" strokeWidth={2.6} />
            Check-list ultime
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Votre feuille de route logistique
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour garder les idées claires au milieu du chaos des
            préparatifs, voici le protocole de compte à rebours idéal à
            imprimer et à suivre pas à pas.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5 relative">
          {/* Horizontal progress line (desktop) */}
          <div
            aria-hidden="true"
            className="hidden md:block absolute top-[40px] left-12 right-12 h-0.5 bg-gradient-to-r from-[#237cb5] via-[#237cb5] to-[#FF7A00]"
          />

          {[
            {
              badge: "J-30",
              sub: "1 mois avant",
              title: "L'heure du grand tri",
              body: "Donnez, vendez ou recyclez ce qui ne vous servira plus. Commandez votre pack complet de cartons sur notre site pour lancer la mise en boîte des éléments hors-saison (déco, livres, vêtements d'hiver).",
              Icon: ShoppingCart,
              color: "#237cb5",
            },
            {
              badge: "J-15",
              sub: "2 semaines avant",
              title: "Sectorisation des pièces",
              body: "Emballez les espaces secondaires (chambre d'amis, bureau, ustensiles de cuisine occasionnels). Identifiez chaque flanc de carton au marqueur avec le nom de sa pièce de destination.",
              Icon: Tags,
              color: "#3b8fc7",
            },
            {
              badge: "J-7",
              sub: "1 semaine avant",
              title: "Concentration du quotidien",
              body: "Mettez sous protection la vaisselle courante, le linge de maison et les objets connectés. Laissez de côté un carton « Kit de survie » pour les dernières 24 heures (papiers, chargeurs, trousse de secours).",
              Icon: CheckCircle2,
              color: "#5fa1d0",
            },
            {
              badge: "J-1",
              sub: "La veille",
              title: "Le scellage final",
              body: "Démontez les derniers meubles, sécurisez les angles et validez l'accès pour le véhicule de transport le lendemain matin.",
              Icon: Lock,
              color: "#FF7A00",
            },
          ].map(({ badge, sub, title, body, Icon, color }) => (
            <article
              key={badge}
              className="relative bg-white rounded-2xl border border-[#cfdbe6] p-6 md:p-7 flex flex-col items-center text-center"
            >
              <div className="relative">
                <span
                  className="flex items-center justify-center h-[80px] w-[80px] rounded-full font-display font-extrabold text-xl border-4 border-white shadow-[0_8px_20px_-12px_rgba(0,0,0,0.25)]"
                  style={{ backgroundColor: color, color: "#fff" }}
                >
                  {badge}
                </span>
              </div>
              <div
                className="mt-4 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10.5px] uppercase tracking-[0.16em] font-bold"
                style={{ backgroundColor: "#EEF3F7", color }}
              >
                {sub}
              </div>
              <span
                className="mt-3 flex items-center justify-center h-9 w-9 rounded-full text-white"
                style={{ backgroundColor: color }}
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={2.4} />
              </span>
              <h3 className="mt-3 font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                {title}
              </h3>
              <p className="mt-2 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </article>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-vanves-cta-packs"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Commander dès J-30
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-vanves-cta-shop"
          >
            <Sparkles className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Garanties enseigne : 2 cards larges ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
            <Award className="h-3.5 w-3.5" strokeWidth={2.6} />
            Garanties enseigne
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi nous déléguer votre sérénité&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Investir dans notre gamme d&apos;équipements professionnels,
            c&apos;est s&apos;assurer une tranquillité d&apos;esprit totale
            basée sur des standards industriels éprouvés.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            {
              kicker: "Zéro intermédiaire",
              title: "Tarifs maîtrisés en direct",
              body: "En gérant nous-mêmes nos stocks franciliens et nos canaux de distribution, nous parvenons à compresser les coûts pour vous offrir une qualité haut de gamme au juste prix.",
              Icon: PiggyBank,
              accent: "#237cb5",
            },
            {
              kicker: "Résistance mécanique certifiée",
              title: "Double cannelure haute densité",
              body: "Nos cartons intègrent des fibres denses à double cannelure capables de supporter les pressions verticales les plus fortes dans le camion, éliminant définitivement les risques d'écrasement ou d'affaissement des boîtes inférieures.",
              Icon: Award,
              accent: "#FF7A00",
            },
          ].map(({ kicker, title, body, Icon, accent }) => (
            <article
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] overflow-hidden flex flex-col"
            >
              <div
                className="h-1.5 w-full"
                style={{ backgroundColor: accent }}
              />
              <div className="p-6 md:p-8 flex-1">
                <div className="flex items-center gap-3">
                  <span
                    className="flex items-center justify-center h-14 w-14 rounded-2xl text-white"
                    style={{ backgroundColor: accent }}
                  >
                    <Icon className="h-6 w-6" strokeWidth={2.4} />
                  </span>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: accent }}>
                    {kicker}
                  </div>
                </div>
                <h3 className="mt-5 font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[14.5px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-vanves-cta-catalog"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir le catalogue complet
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-vanves-cta-packs-2"
          >
            Voir les kits complets
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-vanves-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
